import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from "@mui/material/Switch";
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';

import {
    saveMenuGroupOption,
} from 'store/actions/restaurantMenu';
import { useDispatch } from 'react-redux';
import { noWheelInputProps } from 'util/utils'
import { validateMenuItemName } from 'util/validation'

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
    },
    title: {
        marginBottom: theme.spacing(3),
    },
    form: {
        width: '100%',
    },
    nameField: {
        marginBottom: theme.spacing(2),
    },
    shortNameField: {
        marginBottom: theme.spacing(2),
        width: '200px',
    },
    priceField: {
        width: '150px',
        marginBottom: theme.spacing(2),
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    toggleSection: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    buttonContainer: {
        marginTop: theme.spacing(3),
        '& > button': {
            minWidth: '120px',
        },
    },
    infoIcon: {
        marginLeft: theme.spacing(1),
        fontSize: '1.2rem',
        color: theme.palette.info.main,
        verticalAlign: 'middle',
    },
}));

const NewGroupOption = (props) => {
    const { group } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const saveOption = (values) => dispatch(saveMenuGroupOption(values));
    
    const [hideForWeb, setHideForWeb] = useState(false);
    const [shortName, setShortName] = useState('');
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    const close = () => {
        props.setIsNewOption(false);
    };

    const nameError = validateMenuItemName(name);

    const isSaveButtonDisabled = () => {
        return !name || !!nameError || !price || isNaN(Number(price));
    };

    const save = () => {
        const option = {
            name,
            shortName,
            price: Number(price),
            groupId: group.id,
            menuId: group.menuId,
            default: isDefault,
            sort: group.options ? group.options.length + 1 : 1,
            hideForWeb
        };
        saveOption(option);
        close();
    }

    const handleSwitchChange = (event) => {
        const name = event.target.name;
        const value = event.target.checked;
        if (name === 'hideForWeb') {
            setHideForWeb(value);
        }
    };

    return (
        <Paper className={classes.root} elevation={0}>
            <Typography variant="h6" component="h2" className={classes.title}>
                Add New Option
            </Typography>
            
            <form className={classes.form} noValidate autoComplete="off">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            className={classes.nameField}
                            name="name"
                            fullWidth
                            id="name"
                            label="Option Name"
                            placeholder="Enter the option name"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="outlined"
                            value={name}
                            error={!!nameError}
                            helperText={nameError}
                            onChange={e => setName(e.target.value)}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.shortNameField}
                            label="Short Name"
                            name="shortName"
                            placeholder="Optional short name"
                            type="text"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="outlined"
                            value={shortName}
                            onChange={e => setShortName(e.target.value)}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.priceField}
                            name="price"
                            id="price"
                            label="Price"
                            placeholder="0.00"
                            type="number"
                            inputProps={noWheelInputProps}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={price !== '' && isNaN(Number(price))}
                            helperText={price !== '' && isNaN(Number(price)) ? "Please enter a valid number" : ""}
                            variant="outlined"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider className={classes.divider} />

                <Grid container spacing={2} className={classes.toggleSection}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={hideForWeb}
                                    onChange={handleSwitchChange}
                                    name="hideForWeb"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Hide For Web
                                </Typography>
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isDefault}
                                    onChange={(e) => setIsDefault(e.target.checked)}
                                    color="primary"
                                    name="isDefault"
                                />
                            }
                            label={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2">
                                        Pre-select
                                    </Typography>
                                    <Tooltip title="This item will appear as pre-selected in the menu" arrow placement="right">
                                        <InfoIcon className={classes.infoIcon} />
                                    </Tooltip>
                                </div>
                            }
                        />
                    </Grid>
                </Grid>

                <Grid 
                    container 
                    spacing={2} 
                    className={classes.buttonContainer}
                    justify="flex-end"
                >
                    <Grid item>
                        <Button 
                            variant="outlined" 
                            onClick={close}
                            size="large"
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button 
                            variant="contained"
                            disabled={isSaveButtonDisabled()}
                            color="primary" 
                            onClick={save}
                            size="large"
                        >
                            Save Option
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
};

export default NewGroupOption;